import { z } from 'zod'

// ─── batch-66 verkoop-extractie (FO-493) ─────────────────────────────────────
// Opmaakprofielen voor de factuurextractie: herkenning, veldaanwijzingen en
// voorbeelden per opmaak (bv. de Finqle-verkoopfactuur), deterministische
// naverwerking op de PDF-tekst, zekerheid per veld, de koppeling van
// factuurregels aan dagregels/werkbonnen, de leerlus (correcties -> voorstel ter
// bevestiging) en het kwaliteitsdashboard. Gedeeld tussen API en backoffice.

// ─── Velden ──────────────────────────────────────────────────────────────────

/** De velden van een verkoopconcept waarover zekerheid en correcties worden bijgehouden. */
export const EXTRACTIE_VELDEN = [
  'klant',
  'bv',
  'factuurnummer',
  'factuurdatum',
  'vervaldatum',
  'subtotaal',
  'btwBedrag',
  'btwPercentage',
  'totaal',
  'referentienummer',
  'omschrijving',
  'regels',
] as const
export type ExtractieVeld = (typeof EXTRACTIE_VELDEN)[number]

export const EXTRACTIE_VELD_LABELS: Record<ExtractieVeld, string> = {
  klant: 'Klant',
  bv: 'STREEP-BV',
  factuurnummer: 'Factuurnummer',
  factuurdatum: 'Factuurdatum',
  vervaldatum: 'Vervaldatum',
  subtotaal: 'Bedrag excl. btw',
  btwBedrag: 'Btw-bedrag',
  btwPercentage: 'Btw %',
  totaal: 'Totaal incl. btw',
  referentienummer: 'Referentie / PO',
  omschrijving: 'Omschrijving',
  regels: 'Factuurregels',
}

/** Velden die de reviewer bij goedkeuren kan corrigeren (leerlus). */
export const EXTRACTIE_CORRIGEERBARE_VELDEN = [
  'klant',
  'bv',
  'factuurnummer',
  'factuurdatum',
  'vervaldatum',
  'subtotaal',
  'btwBedrag',
  'btwPercentage',
  'referentienummer',
  'omschrijving',
] as const satisfies readonly ExtractieVeld[]
export type ExtractieCorrigeerbaarVeld = (typeof EXTRACTIE_CORRIGEERBARE_VELDEN)[number]

// ─── Zekerheid per veld ──────────────────────────────────────────────────────

export const FACTUUR_EXTRACTIE_ZEKERHEDEN = ['high', 'medium', 'low'] as const
export type FactuurExtractieZekerheid = (typeof FACTUUR_EXTRACTIE_ZEKERHEDEN)[number]

export const FACTUUR_EXTRACTIE_ZEKERHEID_LABELS: Record<FactuurExtractieZekerheid, string> = {
  high: 'Zeker',
  medium: 'Controleren',
  low: 'Onzeker',
}

/**
 * Waar de waarde vandaan komt:
 *   TEKST_EN_AI = de PDF-tekst en de AI geven dezelfde waarde;
 *   TEKST       = uit de PDF-tekst (de AI gaf niets of iets anders);
 *   AI          = alleen de AI (geen tekstregel gevonden);
 *   MATCH       = klant/BV uit de relatie- of BV-herkenning;
 *   GEEN        = niets gevonden.
 */
export const EXTRACTIE_VELD_BRONNEN = ['TEKST_EN_AI', 'TEKST', 'AI', 'MATCH', 'GEEN'] as const
export type ExtractieVeldBron = (typeof EXTRACTIE_VELD_BRONNEN)[number]

export const EXTRACTIE_VELD_BRON_LABELS: Record<ExtractieVeldBron, string> = {
  TEKST_EN_AI: 'PDF-tekst en AI gelijk',
  TEKST: 'uit de PDF-tekst',
  AI: 'alleen AI',
  MATCH: 'herkenning',
  GEEN: 'niet gevonden',
}

export interface ExtractieVeldZekerheid {
  zekerheid: FactuurExtractieZekerheid
  bron: ExtractieVeldBron
  /** De AI gaf een andere waarde dan de PDF-tekst (de tekst won). */
  aiWeekAf?: boolean
  toelichting?: string
}

export type ExtractieZekerheidPerVeld = Partial<Record<ExtractieVeld, ExtractieVeldZekerheid>>

const veldZekerheidSchema = z.object({
  zekerheid: z.enum(FACTUUR_EXTRACTIE_ZEKERHEDEN),
  bron: z.enum(EXTRACTIE_VELD_BRONNEN),
  aiWeekAf: z.boolean().optional(),
  toelichting: z.string().optional(),
})

/** Defensieve lezer van de opgeslagen zekerheid (oude of kapotte data = leeg). */
export function leesZekerheidPerVeld(json: unknown): ExtractieZekerheidPerVeld {
  if (json == null || typeof json !== 'object' || Array.isArray(json)) return {}
  const uit: ExtractieZekerheidPerVeld = {}
  for (const veld of EXTRACTIE_VELDEN) {
    const parsed = veldZekerheidSchema.safeParse((json as Record<string, unknown>)[veld])
    if (parsed.success) uit[veld] = parsed.data
  }
  return uit
}

// ─── Gestructureerde factuurregels ───────────────────────────────────────────

export const GESTRUCTUREERDE_REGEL_SOORTEN = ['UREN', 'DAGTARIEF', 'KOSTEN', 'OVERIG'] as const
export type GestructureerdeRegelSoort = (typeof GESTRUCTUREERDE_REGEL_SOORTEN)[number]

export const GESTRUCTUREERDE_REGEL_SOORT_LABELS: Record<GestructureerdeRegelSoort, string> = {
  UREN: 'Gewerkte uren',
  DAGTARIEF: 'Dagtarief',
  KOSTEN: 'Kosten',
  OVERIG: 'Overig',
}

/**
 * Eén factuurregel met de onderdelen los: medewerker, datum (of periode),
 * tijden, pauze, eenheden en tarief. Bedragen als getal met twee decimalen
 * (dezelfde vorm als de bestaande extractieregels); `null` = niet aanwezig.
 */
export interface GestructureerdeFactuurRegel {
  soort: GestructureerdeRegelSoort
  /** Leesbare omschrijving zoals hij op de factuurregel landt. */
  omschrijving: string
  medewerker: string | null
  /** Datum (jjjj-mm-dd); bij een periode de eerste dag. */
  datum: string | null
  /** Laatste dag van de periode (jjjj-mm-dd); null bij één dag. */
  datumTot: string | null
  startTijd: string | null
  eindTijd: string | null
  pauzeMinuten: number | null
  tarieftype: string | null
  dienst: string | null
  werkbonNummer: string | null
  eenheden: number | null
  eenheid: 'uur' | 'dag' | 'km' | null
  tarief: number | null
  totaalEx: number | null
  btwPercentage: number | null
  btwBedrag: number | null
  totaalIncl: number | null
}

const getalOfNull = z.number().finite().nullable()
const tekstOfNull = z.string().nullable()

export const gestructureerdeFactuurRegelSchema = z.object({
  soort: z.enum(GESTRUCTUREERDE_REGEL_SOORTEN),
  omschrijving: z.string(),
  medewerker: tekstOfNull,
  datum: tekstOfNull,
  datumTot: tekstOfNull,
  startTijd: tekstOfNull,
  eindTijd: tekstOfNull,
  pauzeMinuten: getalOfNull,
  tarieftype: tekstOfNull,
  dienst: tekstOfNull,
  werkbonNummer: tekstOfNull,
  eenheden: getalOfNull,
  eenheid: z.enum(['uur', 'dag', 'km']).nullable(),
  tarief: getalOfNull,
  totaalEx: getalOfNull,
  btwPercentage: getalOfNull,
  btwBedrag: getalOfNull,
  totaalIncl: getalOfNull,
})

/** Defensieve lezer van opgeslagen gestructureerde regels (kapotte rij = overgeslagen). */
export function leesGestructureerdeRegels(json: unknown): GestructureerdeFactuurRegel[] {
  if (!Array.isArray(json)) return []
  const uit: GestructureerdeFactuurRegel[] = []
  for (const rij of json) {
    const parsed = gestructureerdeFactuurRegelSchema.safeParse(rij)
    if (parsed.success) uit.push(parsed.data)
  }
  return uit
}

// ─── Opmaakprofiel ───────────────────────────────────────────────────────────

/** Ingebouwde regel-parsers (pure functies in `@streep/utils`); het profiel kiest welke gelden. */
export const EXTRACTIE_REGEL_PARSERS = ['FINQLE_GEWERKTE_UREN', 'FINQLE_DAGTARIEF', 'FINQLE_KOSTEN'] as const
export type ExtractieRegelParser = (typeof EXTRACTIE_REGEL_PARSERS)[number]

export const EXTRACTIE_REGEL_PARSER_LABELS: Record<ExtractieRegelParser, string> = {
  FINQLE_GEWERKTE_UREN: 'Gewerkte uren {naam} {datum} {tijden} ({pauze}) - {tarieftype}',
  FINQLE_DAGTARIEF: '{werkbon} - {tarieftype} - {naam} - {n} dagen ({periode})',
  FINQLE_KOSTEN: '{datum} {werkbon} - Kilometervergoeding {naam}: {km} km x tarief',
}

/** Ingebouwde naverwerkers (deterministische lezers van de PDF-tekst). */
export const EXTRACTIE_NAVERWERKERS = ['FINQLE'] as const
export type ExtractieNaverwerker = (typeof EXTRACTIE_NAVERWERKERS)[number]

export const EXTRACTIE_NAVERWERKER_LABELS: Record<ExtractieNaverwerker, string> = {
  FINQLE: 'Finqle-factuuropmaak (kop, partijen, totalen, regels, factoring-IBAN)',
}

/** Waarop een profiel herkend wordt. Kenmerken worden zonder spaties/hoofdletters vergeleken. */
export interface ExtractieProfielHerkenning {
  /** Domeinen van de mailafzender (bv. "voorbeeld.nl"); leeg = niet op afzender herkennen. */
  afzenderDomeinen: string[]
  /** Tekstkenmerken die ALLEMAAL in de PDF-tekst moeten staan. */
  verplichteKenmerken: string[]
  /** Overige tekstkenmerken; `minimaalKenmerken` daarvan moeten voorkomen. */
  kenmerken: string[]
  minimaalKenmerken: number
}

export const extractieProfielHerkenningSchema = z.object({
  afzenderDomeinen: z.array(z.string().trim().min(1).max(253)).max(20),
  verplichteKenmerken: z.array(z.string().trim().min(3).max(200)).max(10),
  kenmerken: z.array(z.string().trim().min(3).max(200)).max(30),
  minimaalKenmerken: z.number().int().min(0).max(30),
})

export const EXTRACTIE_HERKENNING_LEEG: ExtractieProfielHerkenning = {
  afzenderDomeinen: [],
  verplichteKenmerken: [],
  kenmerken: [],
  minimaalKenmerken: 0,
}

/** Waar een veldaanwijzing vandaan komt. */
export const EXTRACTIE_AANWIJZING_BRONNEN = ['SEED', 'HANDMATIG', 'VOORSTEL'] as const
export type ExtractieAanwijzingBron = (typeof EXTRACTIE_AANWIJZING_BRONNEN)[number]

export const EXTRACTIE_AANWIJZING_BRON_LABELS: Record<ExtractieAanwijzingBron, string> = {
  SEED: 'Standaard',
  HANDMATIG: 'Handmatig toegevoegd',
  VOORSTEL: 'Bevestigd voorstel',
}

/** Een aanwijzing geldt voor één veld of voor de hele factuur ('algemeen'). */
export const EXTRACTIE_AANWIJZING_VELDEN = [...EXTRACTIE_VELDEN, 'algemeen'] as const
export type ExtractieAanwijzingVeld = (typeof EXTRACTIE_AANWIJZING_VELDEN)[number]

export interface ExtractieVeldAanwijzing {
  id: string
  veld: ExtractieAanwijzingVeld
  tekst: string
  bron: ExtractieAanwijzingBron
  /** Het bevestigde voorstel waar deze aanwijzing uit voortkwam (alleen bron VOORSTEL). */
  voorstelId?: string | null
  toegevoegdOp?: string | null
}

export const extractieVeldAanwijzingSchema = z.object({
  id: z.string().min(1).max(64),
  veld: z.enum(EXTRACTIE_AANWIJZING_VELDEN),
  tekst: z.string().trim().min(3, 'Vul een aanwijzing in').max(600),
  bron: z.enum(EXTRACTIE_AANWIJZING_BRONNEN),
  voorstelId: z.string().nullable().optional(),
  toegevoegdOp: z.string().nullable().optional(),
})

/** Een voorbeeld (few-shot): wat er op de factuur staat en wat de extractie moet opleveren. */
export interface ExtractieVoorbeeld {
  titel: string
  /** Leesbaar fragment van de factuur (zoals een mens het ziet). */
  tekstFragment: string
  /** De verwachte extractie als JSON-object. */
  extractie: Record<string, unknown>
}

export const extractieVoorbeeldSchema = z.object({
  titel: z.string().trim().min(1).max(120),
  tekstFragment: z.string().trim().min(1).max(4000),
  extractie: z.record(z.string(), z.unknown()),
})

export interface FactuurExtractieProfielDTO {
  id: string
  sleutel: string
  naam: string
  omschrijving: string | null
  actief: boolean
  prioriteit: number
  herkenning: ExtractieProfielHerkenning
  veldAanwijzingen: ExtractieVeldAanwijzing[]
  voorbeelden: ExtractieVoorbeeld[]
  regelParsers: ExtractieRegelParser[]
  naverwerking: ExtractieNaverwerker | null
  updatedAt: string
}

/** Compacte verwijzing naar het profiel op een concept. */
export interface ExtractieProfielRefDTO {
  id: string
  naam: string
}

/** Sleutel van het profiel dat aanwijzingen voor facturen ZONDER opmaakprofiel draagt. */
export const EXTRACTIE_PROFIEL_ALGEMEEN_SLEUTEL = 'algemeen'

/** Body van PATCH /factuur-extractie/profielen/:id. */
export const factuurExtractieProfielUpdateSchema = z.object({
  naam: z.string().trim().min(2, 'Naam is verplicht').max(120).optional(),
  omschrijving: z.string().trim().max(1000).nullable().optional(),
  actief: z.boolean().optional(),
  prioriteit: z.number().int().min(0).max(1000).optional(),
  herkenning: extractieProfielHerkenningSchema.optional(),
  veldAanwijzingen: z.array(extractieVeldAanwijzingSchema).max(60).optional(),
  regelParsers: z.array(z.enum(EXTRACTIE_REGEL_PARSERS)).max(EXTRACTIE_REGEL_PARSERS.length).optional(),
  /**
   * De versie van het profiel die de gebruiker voor zich had (`updatedAt` uit de
   * DTO). Is het profiel intussen gewijzigd (bv. een bevestigd voorstel voegde een
   * aanwijzing toe), dan weigert de API met 409 `PROFIEL_GEWIJZIGD` in plaats van
   * die wijziging te overschrijven. Weglaten = geen controle.
   */
  updatedAt: z.string().min(1).optional(),
})
export type FactuurExtractieProfielUpdateInput = z.infer<typeof factuurExtractieProfielUpdateSchema>

// ─── Werkbon- en verzoekkoppeling ────────────────────────────────────────────

export const EXTRACTIE_REGEL_KOPPEL_STATUSSEN = ['GEKOPPELD', 'MEERDUIDIG', 'GEEN', 'NVT'] as const
export type ExtractieRegelKoppelStatus = (typeof EXTRACTIE_REGEL_KOPPEL_STATUSSEN)[number]

export const EXTRACTIE_REGEL_KOPPEL_STATUS_LABELS: Record<ExtractieRegelKoppelStatus, string> = {
  GEKOPPELD: 'Gekoppeld',
  MEERDUIDIG: 'Meerdere kandidaten',
  GEEN: 'Niet gevonden',
  NVT: 'Geen medewerker of datum op de regel',
}

/** De koppeling van één factuurregel aan dagregels van een werkbon. */
export interface ExtractieRegelKoppelingDTO {
  regelIndex: number
  status: ExtractieRegelKoppelStatus
  werkbonId: string | null
  werkbonNumber: string | null
  dagregelIds: string[]
  zekerheid: 'high' | 'medium' | null
  toelichting: string
}

/** Stand van één werkbon die door de factuurregels geraakt wordt. */
export interface ExtractieWerkbonDekkingDTO {
  werkbonId: string
  werkbonNumber: string
  /** Alle facturabele dagregels van de bon staan op deze factuur. */
  volledigGedekt: boolean
  gedekteDagregels: number
  totaalDagregels: number
  /** Hoe de bon gevonden is: via de regels of alleen via de PO-referentie. */
  via: 'REGELS' | 'PO'
}

export interface ExtractieWerkbonKoppelingDTO {
  regels: ExtractieRegelKoppelingDTO[]
  werkbonnen: ExtractieWerkbonDekkingDTO[]
  /** Werkbonnen waarvan de referentie gelijk is aan de PO op de factuur. */
  poWerkbonNummers: string[]
  /**
   * De hele factuur hoort op de PO-referentie bij deze ene werkbon: precies een
   * werkbon van de klant draagt de PO, geen regel is zeker gekoppeld en geen
   * regel wijst een andere bon aan. Null = de PO beslist niets.
   */
  poKoppeling: { werkbonId: string; werkbonNumber: string } | null
  /** Elke regel met een medewerker of werkbon is eenduidig gekoppeld. */
  volledig: boolean
  samenvatting: string
}

/**
 * Het koppelbesluit: wat het goedkeuren van het concept met de werkbonnen doet,
 * gegeven wat er nu op het formulier staat (klant, BV, referentie, datum en
 * bedragen). Het reviewscherm toont dit en goedkeuren voert precies dit uit: de
 * `vingerafdruk` reist mee met goedkeuren (`koppelingVingerafdruk`). Is het
 * besluit op dat moment anders (de reviewer wijzigde een veld, of een werkbon is
 * intussen aangepast), dan boekt de API niets (409 `KOPPELING_GEWIJZIGD`).
 */
export interface ExtractieKoppelBesluitDTO {
  /** Factuurregels die bij goedkeuren een werkbonnummer krijgen. */
  regelsMetWerkbon: number
  /** De werkbonnummers die op de factuurregels landen. */
  werkbonNummersOpRegels: string[]
  /** Werkbonnen die op gefactureerd worden gezet, voor zover ze dan nog te factureren zijn. */
  teMarkeren: string[]
  /** Werkbonnen die niet op gefactureerd komen, met de reden. */
  overgeslagen: { werkbonNumber: string; reden: string }[]
  /** Waarom er (verder) niets automatisch gebeurt. Null = niets te melden. */
  toelichting: string | null
  /** De factuur wordt geboekt met de regels van de factuur (anders: een samenvattende regel). */
  regelsOvergenomen: boolean
  vingerafdruk: string
}

/** Antwoord van POST /verkoop-mail/concepten/:id/koppeling (de koppeling bij de ingevulde velden). */
export interface VerkoopConceptKoppelingVoorbeeldDTO {
  koppeling: ExtractieWerkbonKoppelingDTO | null
  koppelBesluit: ExtractieKoppelBesluitDTO | null
}

/** Wat het goedkeuren met de gekoppelde werkbonnen deed (naast de FO-470-koppeling). */
export interface ExtractieWerkbonMarkeringDTO {
  /** Werkbonnen die nu op "gefactureerd" staan (FO-449). */
  gemarkeerd: string[]
  /** Werkbonnen die niet gemarkeerd zijn, met de reden. */
  overgeslagen: { werkbonNumber: string; reden: string }[]
  /** Factuurregels die een werkbonnummer kregen. */
  regelsMetWerkbon: number
  /**
   * Waarom er (verder) niets automatisch is gemarkeerd, als dat niet aan een
   * enkele werkbon ligt (bv. niet alle regels gekoppeld). Null = niets te melden.
   */
  toelichting: string | null
}

// ─── Leerlus: correcties en voorstellen ──────────────────────────────────────

/**
 * Welke regelmaat het systeem in een correctie herkent:
 *   LABEL      = de gecorrigeerde waarde staat in de PDF-tekst bij een label
 *                (bv. "Uitgiftedatum") -> voorstel: synoniem op het profiel;
 *   KLANT_KVK  = het KvK-nummer op de factuur hoort bij de gekozen relatie;
 *   KLANT_NAAM = de naam op de factuur hoort bij de gekozen relatie;
 *   BV_NAAM    = de BV-naam op de factuur hoort bij de gekozen BV;
 *   GEEN       = geen regelmaat (alleen geteld voor het dashboard).
 */
export const EXTRACTIE_CORRECTIE_PATRONEN = ['LABEL', 'KLANT_KVK', 'KLANT_NAAM', 'BV_NAAM', 'GEEN'] as const
export type ExtractieCorrectiePatroon = (typeof EXTRACTIE_CORRECTIE_PATRONEN)[number]

export const EXTRACTIE_VOORSTEL_SOORTEN = [
  'PROFIEL_AANWIJZING',
  'KLANT_KVK_ALIAS',
  'KLANT_NAAM_ALIAS',
  'BV_ALIAS',
  'FACTORING_IBAN',
] as const
export type ExtractieVoorstelSoort = (typeof EXTRACTIE_VOORSTEL_SOORTEN)[number]

export const EXTRACTIE_VOORSTEL_SOORT_LABELS: Record<ExtractieVoorstelSoort, string> = {
  PROFIEL_AANWIJZING: 'Aanwijzing op het profiel',
  KLANT_KVK_ALIAS: 'KvK-nummer koppelen aan relatie',
  KLANT_NAAM_ALIAS: 'Naam koppelen aan relatie',
  BV_ALIAS: 'Alias toevoegen aan BV',
  FACTORING_IBAN: 'Factoring-IBAN blokkeren',
}

export const EXTRACTIE_VOORSTEL_STATUSSEN = ['OPEN', 'BEVESTIGD', 'AFGEWEZEN'] as const
export type ExtractieVoorstelStatus = (typeof EXTRACTIE_VOORSTEL_STATUSSEN)[number]

export const EXTRACTIE_VOORSTEL_STATUS_LABELS: Record<ExtractieVoorstelStatus, string> = {
  OPEN: 'Wacht op bevestiging',
  BEVESTIGD: 'Bevestigd',
  AFGEWEZEN: 'Afgewezen',
}

/** Soorten aliassen die de klantherkenning leest (bevestigde voorstellen). */
export const EXTRACTIE_KLANT_ALIAS_SOORTEN = ['KVK', 'NAAM'] as const
export type ExtractieKlantAliasSoort = (typeof EXTRACTIE_KLANT_ALIAS_SOORTEN)[number]

/** Wat een voorstel bij bevestigen doet (vastgelegd bij het aanmaken). */
export type ExtractieVoorstelPayload =
  | { soort: 'PROFIEL_AANWIJZING'; veld: ExtractieVeld; label: string }
  | { soort: 'KLANT_KVK_ALIAS'; kvk: string; clientId: string; clientNaam: string }
  | { soort: 'KLANT_NAAM_ALIAS'; naam: string; clientId: string; clientNaam: string }
  | { soort: 'BV_ALIAS'; naam: string; entityId: string; entityNaam: string }
  | { soort: 'FACTORING_IBAN'; iban: string; tenNameVan: string | null; entityId: string; entityNaam: string }

export interface ExtractieVoorstelDTO {
  id: string
  soort: ExtractieVoorstelSoort
  status: ExtractieVoorstelStatus
  profiel: ExtractieProfielRefDTO | null
  veld: ExtractieVeld | null
  /** Leesbare uitleg: wat er herhaald gecorrigeerd is en wat bevestigen doet. */
  omschrijving: string
  aantalCorrecties: number
  payload: ExtractieVoorstelPayload
  createdAt: string
  beslistOp: string | null
  beslistDoor: string | null
  toelichting: string | null
  /** Mag DEZE gebruiker het voorstel bevestigen (open + de juiste rechten)? */
  magBevestigen: boolean
  /** Waarom bevestigen niet kan (alleen bij een open voorstel zonder de rechten). */
  bevestigUitleg: string | null
  /** Mag DEZE gebruiker het voorstel afwijzen (open + schrijfrechten op Facturen)? */
  magAfwijzen: boolean
}

/** Een vastgelegde herkenning van een relatie (bevestigd klantvoorstel). */
export interface ExtractieKlantAliasDTO {
  id: string
  soort: ExtractieKlantAliasSoort
  /** Het KvK-nummer of de naam zoals die op facturen staat. */
  waarde: string
  /** De relatie waar dit nummer of deze naam bij hoort (null = relatie verwijderd). */
  client: { id: string; naam: string } | null
  vastgelegdOp: string
  vastgelegdDoor: string | null
}

/** Query van GET /factuur-extractie/aliassen. */
export const extractieKlantAliasLijstQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(10),
})

/** Body van POST /factuur-extractie/aliassen/:id/intrekken. */
export const extractieKlantAliasIntrekkenSchema = z.object({
  reden: z.string().trim().max(500).nullable().optional(),
})
export type ExtractieKlantAliasIntrekkenInput = z.infer<typeof extractieKlantAliasIntrekkenSchema>

/** Body van POST /factuur-extractie/voorstellen/:id/afwijzen. */
export const extractieVoorstelAfwijzenSchema = z.object({
  toelichting: z.string().trim().max(500).nullable().optional(),
})
export type ExtractieVoorstelAfwijzenInput = z.infer<typeof extractieVoorstelAfwijzenSchema>

// ─── Instellingen: factoring-IBAN's en de leerdrempel ────────────────────────

/** Standaard aantal gelijke correcties waarna een voorstel ontstaat. */
export const EXTRACTIE_CORRECTIE_DREMPEL_STANDAARD = 3

/** Een rekeningnummer van een factoringmaatschappij, ingesteld per BV. */
export interface FactoringIbanInstelling {
  /** De BV waarvoor dit factoring-IBAN geldt. */
  entityId: string
  /** Genormaliseerd IBAN (hoofdletters, geen spaties). */
  iban: string
  /** Ten name van (bv. de factoringmaatschappij); helpt bij een factuur VAN die partij. */
  tenNameVan: string | null
}

const ibanSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\s+/g, '').toUpperCase())
  .pipe(
    z
      .string()
      .regex(/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/, 'Vul een geldig IBAN in (bv. NL00 BANK 0123 4567 89)'),
  )

export const factoringIbanInstellingSchema = z.object({
  entityId: z.string().min(1, 'Kies een BV'),
  iban: ibanSchema,
  tenNameVan: z.string().trim().max(120).nullable(),
})

export interface FactuurExtractieInstellingen {
  factoringIbans: FactoringIbanInstelling[]
  /** Aantal gelijke correcties waarna het systeem een voorstel doet (nooit stil leren). */
  correctieDrempel: number
}

export const factuurExtractieInstellingenSchema = z.object({
  factoringIbans: z.array(factoringIbanInstellingSchema).max(50),
  correctieDrempel: z
    .number({ error: 'Vul een aantal in' })
    .int('Vul een heel getal in')
    .min(2, 'Minimaal 2 gelijke correcties')
    .max(20, 'Maximaal 20 gelijke correcties'),
})

export const FACTUUR_EXTRACTIE_INSTELLINGEN_STANDAARD: FactuurExtractieInstellingen = {
  factoringIbans: [],
  correctieDrempel: EXTRACTIE_CORRECTIE_DREMPEL_STANDAARD,
}

/** De instellingen zoals het scherm ze toont (met de BV-naam erbij). */
export interface FactuurExtractieInstellingenDTO {
  factoringIbans: (FactoringIbanInstelling & { entityNaam: string | null })[]
  correctieDrempel: number
  /** Versie van de opgeslagen instellingen (null = nog nooit opgeslagen). */
  versie: string | null
}

/**
 * Body van PUT /factuur-extractie/instellingen: de instellingen plus de versie
 * die de gebruiker voor zich had. Zijn de instellingen intussen gewijzigd (bv. een
 * bevestigd voorstel voegde een factoring-IBAN toe), dan weigert de API met 409
 * `INSTELLINGEN_GEWIJZIGD` in plaats van die wijziging te overschrijven.
 */
export const factuurExtractieInstellingenUpdateSchema = factuurExtractieInstellingenSchema.extend({
  versie: z.string().min(1).nullable().optional(),
})
export type FactuurExtractieInstellingenUpdateInput = z.infer<typeof factuurExtractieInstellingenUpdateSchema>

// ─── Kwaliteitsdashboard ─────────────────────────────────────────────────────

/** Eén cel van het dashboard: een profiel in een ISO-week. */
export interface ExtractieKwaliteitWeekDTO {
  /** ISO-week als "2026-W36"; `weekStart` is de maandag (jjjj-mm-dd, Europe/Amsterdam). */
  week: string
  weekStart: string
  concepten: number
  goedgekeurd: number
  /** Goedgekeurde concepten waarbij minstens één veld is gecorrigeerd. */
  metCorrecties: number
  /** Totaal aantal gecorrigeerde velden. */
  correcties: number
  correctiesPerVeld: Partial<Record<ExtractieVeld, number>>
  /** Gemiddeld aandeel velden met hoge zekerheid (0-100), of null zonder meting. */
  zekerheid: number | null
  /** Concepten met minstens één aandachtspunt. */
  metMarkeringen: number
}

export interface ExtractieKwaliteitProfielDTO {
  /** null = facturen zonder opmaakprofiel. */
  profiel: ExtractieProfielRefDTO | null
  weken: ExtractieKwaliteitWeekDTO[]
  totaal: Omit<ExtractieKwaliteitWeekDTO, 'week' | 'weekStart'>
}

export interface ExtractieKwaliteitDTO {
  weken: number
  vanaf: string
  profielen: ExtractieKwaliteitProfielDTO[]
  openVoorstellen: number
  /** Concepten die op "opnieuw uitlezen" wachten (bulk). */
  wachtOpHerlezen: number
}

export const extractieKwaliteitQuerySchema = z.object({
  weken: z.coerce.number().int().min(1).max(52).default(12),
})

// ─── Opnieuw uitlezen ────────────────────────────────────────────────────────

export const EXTRACTIE_HERLEES_WEKEN_MAX = 26

/** Body van POST /factuur-extractie/herverwerken (bulk over de laatste X weken). */
export const extractieHerverwerkenSchema = z.object({
  weken: z
    .number({ error: 'Vul het aantal weken in' })
    .int('Vul een heel aantal weken in')
    .min(1, 'Minimaal 1 week')
    .max(EXTRACTIE_HERLEES_WEKEN_MAX, `Maximaal ${EXTRACTIE_HERLEES_WEKEN_MAX} weken`),
})
export type ExtractieHerverwerkenInput = z.infer<typeof extractieHerverwerkenSchema>

/** Query van GET /factuur-extractie/herverwerken/voorbeeld. */
export const extractieHerverwerkenVoorbeeldQuerySchema = z.object({
  weken: z.coerce.number().int().min(1).max(EXTRACTIE_HERLEES_WEKEN_MAX).default(4),
})

export interface ExtractieHerverwerkenVoorbeeldDTO {
  weken: number
  /** Concepten "Te controleren" in dat venster: die worden opnieuw uitgelezen. */
  teHerlezen: number
  /** Al goedgekeurd, afgewezen of verplaatst: blijven ongemoeid. */
  ongemoeid: number
  /** Staan al in de wachtrij. */
  alInWachtrij: number
}

export interface ExtractieHerverwerkenResultaatDTO {
  inWachtrij: number
  /** AI-tegoed op: de wachtrij wordt verwerkt zodra de AI weer beschikbaar is. */
  aiPauze: boolean
}

/** Uitkomst van "Opnieuw uitlezen" op één concept. */
export interface ExtractieHerleesResultaatDTO {
  profiel: ExtractieProfielRefDTO | null
  /** Velden waarvan de waarde door het opnieuw uitlezen is veranderd. */
  gewijzigdeVelden: ExtractieVeld[]
  markeringen: number
}

// ─── Extractie-informatie op het concept ─────────────────────────────────────

/** Wat het review-scherm over de extractie toont (naast de bestaande velden). */
export interface VerkoopConceptExtractieDTO {
  profiel: ExtractieProfielRefDTO | null
  zekerheid: ExtractieZekerheidPerVeld
  /** Aandeel velden met hoge zekerheid (0-100), of null zonder meting. */
  zekerheidScore: number | null
  regels: GestructureerdeFactuurRegel[]
  koppeling: ExtractieWerkbonKoppelingDTO | null
  /**
   * Wat goedkeuren met de werkbonnen doet bij de waarden van het concept. Null
   * als er niets te beslissen valt (geen opmaakprofiel, of al beoordeeld).
   */
  koppelBesluit: ExtractieKoppelBesluitDTO | null
  /** "Besteld door" op de factuur (contact of afleveradres; nooit de klant). */
  besteldDoor: string | null
  herleesAangevraagdOp: string | null
  laatstUitgelezenOp: string | null
  /** Aantal keer dat dit concept opnieuw is uitgelezen. */
  herleesTeller: number
  kanOpnieuwUitlezen: boolean
}

/** Uitkomst van de proefrun op een PDF-tekst (script, geen AI). */
export interface ExtractieTekstProefDTO {
  profiel: string | null
  velden: Record<string, unknown>
  regels: GestructureerdeFactuurRegel[]
  markeringen: string[]
}
